/**
 * Обращения к «карману» — фразам-чанкам пользователя.
 *
 * Список отдаёт не весь карман, а очередь на повторение: фразы, у
 * которых подошла дата. Именно их видит дашборд.
 */

import type { AddPocketPhrase, PocketPage, PocketPhrase, SmartReviewDeck } from '@aptile/shared';

import { apiRequest, jsonBody } from './client';

/** Очередь на повторение — ближайшие к показу слова первыми. */
export function fetchDueVocabulary(
  userId: string,
): Promise<readonly PocketPhrase[]> {
  const query = new URLSearchParams({ userId });
  return apiRequest<readonly PocketPhrase[]>(
    `/api/vocabulary?${query.toString()}`,
  );
}

/** Кладёт фразу в карман; повторное добавление обновляет перевод и контекст. */
export function addPocketPhrase(input: AddPocketPhrase): Promise<PocketPhrase> {
  return apiRequest<PocketPhrase>('/api/vocabulary', {
    method: 'POST',
    body: jsonBody(input),
  });
}

export interface PocketPageRequest {
  readonly limit?: number;
  /** `nextCursor` прошлой страницы; без него — первая страница. */
  readonly cursor?: string;
  /** Поиск по фразе и переводу. */
  readonly q?: string;
}

/** Страница кармана: фразы с контекстом истории, новые первыми. */
export function fetchPocketPage(userId: string, request: PocketPageRequest = {}, signal?: AbortSignal): Promise<PocketPage> {
  const query = new URLSearchParams({ userId });
  if (request.limit !== undefined) query.set('limit', String(request.limit));
  if (request.cursor !== undefined) query.set('cursor', request.cursor);
  if (request.q !== undefined && request.q.trim().length > 0) query.set('q', request.q.trim());
  return apiRequest<PocketPage>(`/api/vocabulary/pocket?${query.toString()}`, { signal });
}

/** Оценка повторения FSRS: 1 — «ещё раз», 3 — «знаю». */
export type PocketRating = 1 | 2 | 3 | 4;

/** Ответ на карточку кармана — сервер сдвигает дату следующего показа. */
export function reviewPocketPhrase(userId: string, itemId: string, rating: PocketRating): Promise<unknown> {
  return apiRequest<unknown>('/api/vocabulary/review', {
    method: 'POST',
    body: jsonBody({ userId, itemId, rating }),
  });
}

/** Умное повторение: фразы, которые ученик забывает (FSRS), слабые первыми. */
export function fetchSmartReview(userId: string, limit: number): Promise<SmartReviewDeck> {
  const query = new URLSearchParams({ userId, limit: String(limit) });
  return apiRequest<SmartReviewDeck>(`/api/vocabulary/pocket/smart?${query.toString()}`, { cache: 'no-store' });
}

/** «Перемешать всё»: случайная колода из всего кармана. */
export function fetchShuffledPocket(userId: string, limit: number): Promise<readonly PocketPhrase[]> {
  const query = new URLSearchParams({ userId, limit: String(limit) });
  return apiRequest<readonly PocketPhrase[]>(`/api/vocabulary/pocket/shuffle?${query.toString()}`, { cache: 'no-store' });
}
